import { useQuery } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { useDebounce } from "../useDebounce";
import { useTenant } from "../../contexts/TenantContext";
import {
  locationAutocompleteService,
  AutocompleteSuggestion,
  AutocompleteRequest,
} from "../../api/locationAutocomplete";

interface UseLocationAutocompleteProps {
  debounceMs?: number;
  limit?: number;
  types?: AutocompleteRequest["types"];
}

export const useLocationAutocomplete = ({
  debounceMs = 300,
  limit = 10,
  types,
}: UseLocationAutocompleteProps = {}) => {
  const { config } = useTenant();
  const [inputValue, setInputValue] = useState("");
  const debouncedValue = useDebounce(inputValue, debounceMs);
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<AutocompleteSuggestion | null>(null);

  // Only fetch suggestions if we have at least 2 characters
  const enabled = debouncedValue.length >= 2;

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: [
      "locationAutocomplete",
      debouncedValue,
      limit,
      types,
      config?.id,
    ],
    queryFn: async ({ signal }) => {
      const request: AutocompleteRequest = {
        query: debouncedValue,
        tenant_id: config?.id || "",
        limit,
        types,
      };
      return await locationAutocompleteService.getAutocompleteResults(
        request,
        signal
      );
    },
    enabled,
    staleTime: 1000 * 60 * 30, // Cache results for 30 minutes
    gcTime: 1000 * 60 * 60, // Keep unused data in cache for 1 hour
  });

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    // Clear selected suggestion when input changes
    setSelectedSuggestion(null);
  }, []);

  const handleSelectSuggestion = useCallback(
    (suggestion: AutocompleteSuggestion) => {
      setSelectedSuggestion(suggestion);
      setInputValue(suggestion.display);
    },
    []
  );

  const clearInput = useCallback(() => {
    setInputValue("");
    setSelectedSuggestion(null);
  }, []);

  return {
    inputValue,
    suggestions: data?.suggestions || [],
    isLoading: isLoading || isFetching,
    error,
    handleInputChange,
    handleSelectSuggestion,
    selectedSuggestion,
    clearInput,
  };
};
